import React, { useCallback, useEffect, useState } from 'react';
import { usePopper } from 'react-popper';
import styled, { css } from 'styled-components';
import { useObserver } from '../../../../CodesThatsGottaGoSomewhereElse';
import { Box, Float, usePrompt } from '../../../../ui';
import { useResolve } from '../../../Container';
import { UIField, UIFieldProps } from '../../../canvas/entities/UIField';
import { PopperInner, PopperMount, PopperPortal } from '../../../canvas/ui/PopperElements';
import { AdapterInfo } from '../../AdapterInfo';
import { Camera } from '../../Camera';
import { EditorSession } from '../../EditorSession';

const PopperButton = styled.div<{ active?: boolean }>`
    position: absolute;
    inset: 0;

    border-radius: 2px;
    &:hover {
        box-shadow: 0 0 0 3px rgba(164,202,254,.45);
        cursor: pointer;
    }
    ${p => !p.active ? '' : css`
        box-shadow: 0 0 0 3px rgba(164,202,254,.45);
    `}
`;

export type PopperFieldEditorContentProps = { field: UIField, onClose: () => void };
export type PopperFieldEditorContent = React.ComponentType<PopperFieldEditorContentProps>;

export const PopperFieldEditor = (Content: PopperFieldEditorContent) =>
    ({ active, field }: UIFieldProps) => {

        const session = useResolve(EditorSession);
        const cameraState = useObserver(useResolve(Camera));

        const [refElmt, setRefElmt] = useState(null);
        const [popElmt, setPopElmt] = useState(null);

        const { styles, attributes, forceUpdate } = usePopper(refElmt, popElmt, {
            placement: 'right-start',
        });

        useEffect(() => {
            if (forceUpdate) forceUpdate();
        }, [cameraState]);

        const [open, setOpen] = useState(active);
        const onOpen = useCallback(() => setOpen(!open), [open, setOpen]);

        const promptRef = usePrompt({
            ignoreEnter: true,
            onAction: () => setOpen(false)
        });

        useEffect(() => {
            const curr = session.get(AdapterInfo);
            if (!open || curr?.name === field.name) return;
            session.patch(AdapterInfo, { name: field.name });
        }, [open]);

        return <>
            <PopperButton ref={setRefElmt} active={open} onClick={onOpen} />
            {open && <PopperPortal>
                <PopperMount ref={setPopElmt} className="ignore-adapter" style={styles.popper} {...attributes.popper}>
                    <PopperInner ref={promptRef} direction="right">
                        <Box padding="0 .5">
                            <Float shadow="lg">
                                <Content field={field} onClose={() => setOpen(false)} />
                            </Float>
                        </Box>
                    </PopperInner>
                </PopperMount>
            </PopperPortal>}
        </>;
    };
